import React from 'react'
import { Text, View, StyleSheet, Linking } from 'react-native'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import ReceiptStore from './ReceiptStore'
import { invoiceStatus } from '@linqpal/models/src/dictionaries'
import { Divider } from 'react-native-paper'
import { parsePhoneNumber } from 'libphonenumber-js'
import { BlueTapeIconDark, PhoneIconBlue } from '../../../../assets/icons'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import { DetailsRowItem } from '../PayInvoice/DetailsRowItem'
import { currencyMask, dateMask } from '../../../../utils/helpers/masking'

interface PaymentDetailsProps {
  showCollectedLabel?: boolean
}

const BTCreditWithIcon = () => {
  const { t } = useTranslation('global')
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
      <BlueTapeIconDark style={{ height: 18, width: 18, marginRight: 4 }} />
      <Text>{t('BtCredit')}</Text>
    </View>
  )
}

export const PaymentDetails: React.FC<PaymentDetailsProps> = observer(
  ({ showCollectedLabel = true }) => {
    const { t } = useTranslation('global')
    const { invoice, successTransaction, isPaidWithCredit } = ReceiptStore
    const builderCreatedInvoice = !!invoice?.supplierInvitationDetails
    const phoneNumber = builderCreatedInvoice
      ? invoice?.supplierInvitationDetails?.phone
      : invoice?.company?.phone

    const paymentMethod = () => {
      const accountNumber = successTransaction?.metadata?.accountNumber || ''
      return successTransaction?.payment_method === 'card'
        ? `${successTransaction?.metadata?.network || ''} ${
            successTransaction?.metadata?.cardLastFour
              ? ' ****' + successTransaction?.metadata?.cardLastFour
              : ''
          }`
        : `${accountNumber}`
    }

    const handlePhonePress = () => {
      if (phoneNumber) {
        Linking.openURL(`tel:${phoneNumber}`)
      }
    }

    const formattedPhone = phoneNumber
      ? parsePhoneNumber(phoneNumber, 'US').formatInternational().slice(3)
      : null
    const formattedPhoneWithParentheses = formattedPhone
      ? `(${formattedPhone.slice(0, 3)})${formattedPhone.slice(3)}`
      : null

    return (
      <>
        <View style={styles.nameAmountContainer}>
          <Text style={styles.text}>
            {' '}
            {builderCreatedInvoice
              ? invoice.supplierInvitationDetails?.name
              : invoice.company?.name}{' '}
          </Text>
          <Text style={styles.text}>
            {' '}
            {currencyMask(invoice.amountPaid + invoice.lateFee)}{' '}
          </Text>
        </View>
        {phoneNumber && (
          <View style={styles.phoneAmountPaidContainer}>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <PhoneIconBlue
                style={{
                  alignSelf: 'center',
                  width: 13,
                  height: 13,
                  marginTop: 2,
                }}
              />
              <Text style={styles.phone} onPress={handlePhonePress}>
                {formattedPhoneWithParentheses}
              </Text>
            </View>
            <Text style={styles.amountpaid}>
              {t('InvoiceReceipt.AmountPaid')}
            </Text>
          </View>
        )}
        <Divider style={{ marginTop: 16, marginBottom: 14 }} />
        {showCollectedLabel && invoice.status === invoiceStatus.collected && (
          <DetailsRowItem
            leftText={t('InvoiceReceipt.status')}
            rightText={
              <Text style={styles.collected}>
                {t('InvoiceReceipt.collected-by-supplier')}
              </Text>
            }
          />
        )}
        {!!invoice.operation?.metadata?.achDiscount && (
          <>
            <DetailsRowItem
              leftText={t('InvoiceReceipt.InvoiceAmount')}
              rightText={currencyMask(invoice.total_amount)}
            />
            <DetailsRowItem
              leftText={t('InvoiceReceipt.Discount')}
              rightText={currencyMask(invoice.operation.metadata.achDiscount)}
            />
          </>
        )}

        <DetailsRowItem
          leftText={`Invoice #${invoice.invoice_number}`}
          rightText={`Due on ${dateMask(invoice.invoice_due_date)}`}
        />
        {!!invoice.lateFee && (
          <>
            <DetailsRowItem
              leftText={t('InvoiceReceipt.InvoiceAmount')}
              rightText={currencyMask(invoice.total_amount)}
            />
            <DetailsRowItem
              leftText={t('InvoiceReceipt.LateInterestCharge')}
              rightText={currencyMask(invoice.lateFee)}
            />
          </>
        )}
        {invoice.addressType === 'Delivery' ? (
          <DetailsRowItem
            leftText={`${t('InvoiceDetails.deliver-to')}:`}
            rightText={invoice.address}
          />
        ) : invoice.addressType === 'Pickup' ? (
          <DetailsRowItem leftText={t('InvoiceDetails.pick-up')} />
        ) : null}
        <DetailsRowItem
          leftText={`${t('InvoiceReceipt.payment_method')}:`}
          rightText={isPaidWithCredit ? <BTCreditWithIcon /> : paymentMethod()}
        />
        {!isPaidWithCredit && (
          <DetailsRowItem
            leftText={`${t('InvoiceReceipt.paid_on')}:`}
            rightText={
              successTransaction?.date
                ? dateMask(successTransaction.date)
                : null
            }
          />
        )}
      </>
    )
  },
)

const styles = StyleSheet.create({
  nameAmountContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  amountpaid: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
    color: '#99ADBA',
  },
  phoneAmountPaidContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  phone: {
    fontSize: 14,
    lineHeight: 21,
    color: commonColors.accentText,
    fontWeight: '500',
    marginLeft: 5,
  },
  text: {
    fontSize: 18,
    fontWeight: '600',
    color: '#335C75',
  },
  collected: {
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 21,
    color: '#23BD86',
  },
})
